// 無音・環境ノイズだけの音声区間を文字起こしモデルに渡すと、実際には
// 発話されていない定型句（Whisper系の既知の幻覚）が生成されることがある。
// また、文字起こしモデルへ渡す指示文は「直前の文脈」として扱われるため、
// 指示文そのものが発話として出力され、実際の回答の末尾に連結されることがある。
// これらを回答として扱うと面接が勝手に進む・フィードバック対象が汚れるため、
// 候補者の文字起こしからは該当する文を取り除いたうえで回答として扱う。
//
// 「ありがとうございました」などの汎用的な感謝表現は、面接中の正当な
// 単独発話としても成立しうるが、単独では回答としての情報量がないため
// 取り除く判断を意図的に受け入れている（他の語と同じ文に含まれる発話は残る）。
const HALLUCINATION_PHRASES = [
  "ご視聴ありがとうございました",
  "ご視聴ありがとうございます",
  "ご清聴ありがとうございました",
  "ご清聴ありがとうございます",
  "最後までご視聴ありがとうございました",
  "チャンネル登録お願いします",
  "チャンネル登録よろしくお願いします",
  "ありがとうございました",
  "ありがとうございます",
  "お疲れ様でした",
  "お疲れさまでした",
];

// バックエンドで廃止した文字起こし指示文。旧設定のまま接続中のセッションでも
// 混入を取り除けるよう、文全体と句点区切りの断片を除去対象に残しておく。
const TRANSCRIPTION_PROMPT_ECHOES = [
  "日本語の採用面接",
  "職種名、企業名、業界用語を文脈に沿って文字起こしする",
];

// 「ご視聴ありがとうございました。」のような句読点・空白の変種を
// 取りこぼさないよう、照合前に記号類を除去する。文字起こしは半角の
// 句読点を返すこともあるため、全角・半角の両方を対象にする
const NORMALIZE_PATTERN = /[\s、。，．,.・…!?！？「」『』()（）]/g;

// 面接官の締め文言の照合にも同じ正規化を使う。書き分けると、片方だけ
// 半角の句点に対応するといった食い違いが起きる
export function normalizeForMatching(transcript: string) {
  return transcript.replace(NORMALIZE_PATTERN, "");
}

// 幻覚は同一定型句の繰り返しや複数定型句の連結として出ることもあるため、
// 文単位に分割し、記号を除いた形が除去対象と一致する文だけを落とす。
// 長い句から先に取り除き、部分一致による取り残しを防ぐ
const NORMALIZED_REMOVALS = [
  ...new Set(
    [...HALLUCINATION_PHRASES, ...TRANSCRIPTION_PROMPT_ECHOES].map(
      normalizeForMatching,
    ),
  ),
].sort((a, b) => b.length - a.length);

// 文末記号を含めた文の並びとして取り出す（後読みは Safari の対応が新しいため使わない）
const SENTENCE_PATTERN = /[^。．.!?！？\n]*[。．.!?！？\n]|[^。．.!?！？\n]+/g;

function isRemovableSentence(sentence: string) {
  let remainder = normalizeForMatching(sentence);
  if (remainder.length === 0) return true;
  for (const phrase of NORMALIZED_REMOVALS) {
    remainder = remainder.split(phrase).join("");
  }
  return remainder.length === 0;
}

// 文字起こしから捏造された文を取り除いた回答を返す。
// 取り除いた結果として内容が残らない場合は null を返し、呼び出し側で
// 無効な回答として扱う。
export function sanitizeCandidateTranscript(
  transcript: string | null | undefined,
): string | null {
  if (!transcript) return null;
  const sentences = transcript.match(SENTENCE_PATTERN) ?? [];
  const kept = sentences
    .filter((sentence) => !isRemovableSentence(sentence))
    .join("")
    .trim();
  return kept.length > 0 ? kept : null;
}
